import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import type { PremiseConfig } from "@shared/types/foyer";
import type {
  ChainOfferFunnelRow,
  ChainOfferFunnelSummary,
  OfferFunnelRow,
  OfferFunnelSummary,
  OfferFunnelTotals,
} from "@shared/types/offerFunnel";
import { useCan } from "../auth/AuthContext";
import { foyerApi } from "../http/foyer";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { formatPercent } from "../components/engagementFormat";
import DemoPaymentsBanner from "../components/offers/DemoPaymentsBanner";
import OfferFunnelStrip, {
  FunnelLegend,
} from "../components/offers/OfferFunnelStrip";
import OfferRevenueTrend from "../components/offers/OfferRevenueTrend";
import {
  ENGAGED_PROXY_NOTE,
  GRAIN_NOTE,
  PIPELINE_NOTE,
  REVENUE_RULE_NOTE,
  formatMoneyTotals,
  formatPerMille,
  moneyIsEmpty,
  perMilleHint,
  primaryMoney,
} from "../components/offers/offerRevenueFormat";
import { tokens } from "../theme";

/**
 * Revenue — "this screen earned 42 300 kr" (`POS-12`).
 *
 * The page a salesperson opens after the guest demo and a hotel opens once a
 * month to decide whether to keep paying. One number leads; everything else
 * exists to make that number believable.
 *
 * ## The editorial rule, which is also the product
 *
 * **Revenue comes from accepted requests, never from impressions**
 * (`docs/room-offers.md` §5). An impression is not revenue. A request nobody
 * accepted has earned nothing. `pending` money is drawn as pipeline, in its own
 * figure with its own label, and is never added to the headline. A refund
 * reverses revenue by construction — the status changes and the sum drops —
 * so there is no adjustment row here and none should be invented.
 *
 * Where a number is a proxy it says so: `engagedRate` is a proxy for attention,
 * not a conversion rate, and the copy carries that sentence verbatim rather
 * than paraphrasing it into something softer.
 *
 * ## What the drop-offs are for
 *
 * The totals are not the interesting part. An offer with 4 000 impressions and
 * 2 requests is telling the hotel to change the picture or the price, and that
 * is the sentence the funnel strips exist to make legible. Revenue per 1000
 * impressions sits beside them so a 450 kr spa slot and a 95 kr breakfast can
 * be compared on one axis instead of by gut feel.
 *
 * ## Two layers, one page
 *
 * A location scoped → that hotel, with its day-by-day trend. A customer scoped
 * and NO location → every hotel of the chain, ranked best-earning first
 * (`CHAIN_CAPABLE_SECTIONS`). Care premises never appear: offers are hotel-only
 * for v1 (§10).
 */

const RANGES = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 365, label: "Last 12 months" },
];

/** How many offers get a funnel strip before the rest go to the table + CSV. */
const STRIP_LIMIT = 8;

/**
 * The window from the URL, or the default — never a value the picker cannot
 * show. The server clamps `?days` to 1-400 and would answer a 45-day report
 * quite happily; the Range control has no option for it, so it would render
 * blank and the operator would be reading a window nothing on the page names.
 */
export function windowFromUrl(raw: string | null): number {
  const n = Number(raw);
  return RANGES.some((r) => r.value === n) ? n : 30;
}

interface Props {
  /** Exactly one of the two. A location, or the whole customer. */
  premise?: PremiseConfig;
  companyId?: string;
  companyName?: string;
}

export default function OfferRevenuePage({
  premise,
  companyId,
  companyName,
}: Props) {
  // All three hooks run on every render: `a() || b()` short-circuits, and a
  // hook that runs conditionally is the rule React breaks loudest about. The
  // three keys mirror the route's `requireAnyPermission` exactly — whoever
  // decides what is for sale and whoever works the queue may both read what it
  // made, and `canEditLayout` is the grandfather clause for operators whose
  // stored permissions predate the offers keys.
  const holdsOffers = useCan("canManageOffers");
  const holdsQueue = useCan("canHandleGuestRequests");
  const holdsLayout = useCan("canEditLayout");
  const mayRead = holdsOffers || holdsQueue || holdsLayout;

  const [params, setParams] = useSearchParams();
  // In the URL so a revenue report is a link somebody can paste into an email —
  // the single most common thing anyone does with one of these. Validated
  // against the four windows on offer rather than trusted: a pasted `?days=45`
  // would leave the Range control with no matching option, so an operator would
  // read a report whose window is not written anywhere on it.
  const days = windowFromUrl(params.get("days"));
  const setDays = useCallback(
    (value: string) => {
      const next = new URLSearchParams(params);
      next.set("days", value);
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  const [data, setData] = useState<
    OfferFunnelSummary | ChainOfferFunnelSummary | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const premiseId = premise?.premiseId;

  useEffect(() => {
    if (!mayRead) return;
    if (!premiseId && !companyId) return;
    let cancelled = false;
    // The old report is DISCARDED before the new one is asked for. This
    // component stays mounted across a scope change and a range change, and a
    // page that kept the previous summary would draw Hasselbacken's revenue
    // under Maple's header — with an Export CSV button already pointing at
    // Maple, so the file and the figures would disagree. A spinner is the
    // honest state while the answer is unknown.
    setData(null);
    setError(null);
    setLoading(true);
    void (async () => {
      try {
        const summary = premiseId
          ? await foyerApi.offerFunnel(premiseId, days)
          : await foyerApi.chainOfferFunnel(companyId!, days);
        if (!cancelled) {
          setData(summary);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mayRead, premiseId, companyId, days]);

  if (!mayRead) return <NotYourReport />;

  const csvUrl = premiseId
    ? foyerApi.offerFunnelCsvUrl(premiseId, days)
    : companyId
      ? foyerApi.chainOfferFunnelCsvUrl(companyId, days)
      : "#";

  const totals = data?.totals;
  // Nothing shown and nothing asked for: the teaching state, not a wall of
  // confident zeros.
  const hasAnything =
    !!totals && totals.impressions + totals.requests + totals.opens > 0;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Promotions"
        title="Revenue"
        subtitle={
          premise
            ? "What the televisions put in front of guests, and what somebody at reception accepted."
            : `Every hotel ${companyName ?? "this customer"} runs, best-earning first.`
        }
        actions={
          <>
            <TextField
              select
              size="small"
              label="Range"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              data-testid="revenue-range"
              sx={{ minWidth: 170 }}
            >
              {RANGES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
            {/* A plain link, not a fetch: the browser streams the file and
                carries the session cookie itself. */}
            <Button
              component="a"
              href={csvUrl}
              disabled={!data}
              startIcon={<DownloadOutlinedIcon />}
              variant="outlined"
              data-testid="revenue-export"
            >
              Export CSV
            </Button>
          </>
        }
      />

      {/* POS-8, §6: a revenue figure produced by an always-accepts provider must
          never be screenshotted without the caveat attached. One component, one
          server verdict — there is deliberately no second banner. */}
      <DemoPaymentsBanner />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="revenue-error">
          {error}
        </Alert>
      )}
      {loading && !data && <CircularProgress size={24} />}

      {data && !hasAnything && <TeachingEmptyState />}

      {data && hasAnything && (
        <Stack spacing={3}>
          <HeadlineCard
            totals={data.totals}
            from={data.from}
            to={data.to}
            premiseCount={
              "premiseCount" in data ? data.premiseCount : undefined
            }
          />

          {"series" in data && (
            <SectionCard
              title="The window, day by day"
              caption="Three measures, three scales, one date axis. They are never overlaid on one baseline: money and impressions are different facts about different grains, and a shared axis would read as cause and effect."
            >
              <OfferRevenueTrend
                series={data.series}
                currency={primaryMoney(data.totals.revenue)?.currency ?? null}
              />
            </SectionCard>
          )}

          {"premises" in data && <PremiseRanking data={data} />}

          <SectionCard
            title="Offer by offer — where guests drop off"
            caption="Ranked by revenue. Each step is a share of the impressions above it, so the fall from a screen full of people to a request somebody accepted is visible as a shape rather than as arithmetic."
            action={<FunnelLegend />}
          >
            <OfferStrips
              rows={data.offers}
              premiseCount={"premises" in data ? data.premiseCount : undefined}
            />
          </SectionCard>

          <SectionCard
            title="Every number"
            caption={GRAIN_NOTE}
            action={
              <Typography sx={{ fontSize: 12, color: tokens.inkFaint }}>
                Matches the CSV export, column for column.
              </Typography>
            }
          >
            <OfferTable rows={data.offers} chain={"premises" in data} />
          </SectionCard>
        </Stack>
      )}
    </PageContainer>
  );
}

/* -------------------------------------------------------------------------- */
/* The headline                                                               */
/* -------------------------------------------------------------------------- */

/**
 * One number, and the four sentences that make it believable.
 *
 * Revenue and pipeline are two figures with two labels and no arithmetic
 * between them. Putting pipeline in the hero — or under it as "including" —
 * is how "we nearly made this" becomes "we made this" in a slide three weeks
 * later.
 */
function HeadlineCard({
  totals,
  from,
  to,
  premiseCount,
}: {
  totals: OfferFunnelTotals;
  from: string;
  to: string;
  premiseCount?: number;
}) {
  const earnedNothing = moneyIsEmpty(totals.revenue);
  return (
    <Card variant="outlined" sx={{ p: 3 }} data-testid="revenue-headline">
      <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
        {premiseCount === undefined
          ? "This screen earned"
          : `These ${premiseCount} ${premiseCount === 1 ? "hotel" : "hotels"} earned`}
        , {from} → {to}
      </Typography>
      <Typography
        sx={{
          fontSize: 48,
          lineHeight: 1.1,
          fontWeight: 700,
          color: tokens.ink,
          mt: 0.5,
        }}
        data-testid="revenue-total"
      >
        {earnedNothing ? "0" : formatMoneyTotals(totals.revenue)}
      </Typography>
      <Typography sx={{ fontSize: 13, color: tokens.inkMuted, mt: 0.5 }}>
        {earnedNothing
          ? "Nothing priced was accepted in this window. That is a measurement, not a missing number — a free request counts as a request and adds no money."
          : REVENUE_RULE_NOTE}
      </Typography>

      <Stack
        direction="row"
        spacing={5}
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 3 }}
      >
        <Figure
          label="Pipeline — not revenue"
          value={
            moneyIsEmpty(totals.pipeline)
              ? "0"
              : formatMoneyTotals(totals.pipeline)
          }
          hint={PIPELINE_NOTE}
          testId="revenue-pipeline"
        />
        <Figure
          label="Revenue per 1000 impressions"
          value={formatPerMille(
            totals.revenuePerMille,
            totals.revenuePerMilleCurrency
          )}
          hint={perMilleHint(totals.revenuePerMille)}
          testId="revenue-per-mille"
        />
        <Figure
          label="Impressions"
          value={totals.impressions.toLocaleString("sv-SE")}
          hint="Times an offer was on a screen for at least a second. Never revenue."
        />
        <Figure
          label={`Engaged (≥5s) · ${formatPercent(totals.engagedRate)}`}
          value={totals.engaged.toLocaleString("sv-SE")}
          hint={ENGAGED_PROXY_NOTE}
          testId="revenue-engaged"
        />
        <Figure
          label="Requested"
          value={totals.requests.toLocaleString("sv-SE")}
          hint="Every request raised from a room in this window, whatever became of it."
        />
        <Figure
          label="Accepted"
          value={totals.accepted.toLocaleString("sv-SE")}
          hint="Accepted or completed. Payment status is never consulted: an accepted charge-to-room order is money owed whether or not the folio line has posted."
        />
        <Figure
          label="Declined, cancelled, refunded"
          value={(
            totals.declined +
            totals.cancelled +
            totals.refunded
          ).toLocaleString("sv-SE")}
          hint="All three earn zero. A refund reverses revenue by construction: the status changes and the sum above drops."
          testId="revenue-lost"
        />
      </Stack>
    </Card>
  );
}

function Figure({
  label,
  value,
  hint,
  testId,
}: {
  label: string;
  value: string;
  hint?: string;
  testId?: string;
}) {
  const body = (
    <Box>
      <Typography
        sx={{ fontSize: 22, fontWeight: 700, color: tokens.ink }}
        data-testid={testId}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
        {label}
      </Typography>
    </Box>
  );
  return hint ? (
    <Tooltip title={hint} placement="bottom-start">
      {body}
    </Tooltip>
  ) : (
    body
  );
}

/* -------------------------------------------------------------------------- */
/* Offer rows                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * How many properties contributed to a chain row, or null on a premise row.
 *
 * `ChainOfferFunnelRow extends OfferFunnelRow`, so both views hand these
 * components the wider type and this is the one place that asks whether the
 * extra field is there. Narrowing with `in` inside JSX types the field as
 * `unknown`, which is how a perfectly good number ends up unrenderable.
 */
function premiseCountOf(row: OfferFunnelRow): number | null {
  const value = (row as Partial<ChainOfferFunnelRow>).premiseCount;
  return typeof value === "number" ? value : null;
}

function OfferStrips({
  rows,
  premiseCount,
}: {
  rows: OfferFunnelRow[];
  premiseCount?: number;
}) {
  const shown = rows.slice(0, STRIP_LIMIT);
  // Every per-1000 bar is drawn against the best one, so the tail stays
  // readable instead of collapsing into a row of invisible slivers.
  const bestPerMille = Math.max(0, ...rows.map((r) => r.revenuePerMille ?? 0));

  if (rows.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
        Guests saw something, but no offer in the catalogue matched it. Nothing
        to break down yet.
      </Typography>
    );
  }

  return (
    <Stack
      spacing={3}
      divider={<Box sx={{ borderTop: `1px solid ${tokens.divider}` }} />}
    >
      {shown.map((row) => (
        <Box key={row.offerId}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="baseline"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 1.5 }}
          >
            <Typography sx={{ fontWeight: 700, color: tokens.ink }}>
              {row.label}
            </Typography>
            {row.scope && row.scope !== "premise" && (
              <Chip
                size="small"
                label={row.scope === "company" ? "Chain offer" : "Global offer"}
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
            <ReachNote
              reached={premiseCountOf(row)}
              premiseCount={premiseCount}
            />
            <Box sx={{ flex: 1 }} />
            <PerMilleBar
              value={row.revenuePerMille}
              currency={row.revenuePerMilleCurrency}
              best={bestPerMille}
              offerId={row.offerId}
            />
          </Stack>
          <OfferFunnelStrip row={row} />
        </Box>
      ))}
      {rows.length > shown.length && (
        <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
          +{rows.length - shown.length} more offers in the table below and in
          the CSV.
        </Typography>
      )}
    </Stack>
  );
}

/**
 * "3 of 4 hotels contributed" — how far a chain offer actually reached.
 *
 * A chain-scope offer is authored once and read by every hotel, so its row is
 * the sum of the hotels that produced anything. Without this line a campaign
 * that only one property ever showed reads identically to one the whole group
 * ran, which is exactly the comparison a group bought the chain view to make.
 */
function ReachNote({
  reached,
  premiseCount,
}: {
  reached: number | null;
  premiseCount?: number;
}) {
  if (reached === null) return null;
  const total = premiseCount ?? reached;
  return (
    <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
      {reached} of {total} {total === 1 ? "hotel" : "hotels"} contributed
    </Typography>
  );
}

/**
 * Revenue per 1000 impressions, as a number and as a bar against the best row.
 *
 * The three states are three different things and are drawn as three different
 * things: a value, an honest `0` (shown, earned nothing — the row an operator
 * is hunting for), and `—` for the two cases where the ratio is genuinely
 * unknowable. A `0` rendered as a dash would hide the worst performer in the
 * report, which is the one bug this column exists to avoid.
 */
function PerMilleBar({
  value,
  currency,
  best,
  offerId,
}: {
  value: number | null;
  currency: string | null;
  best: number;
  offerId: string;
}) {
  return (
    <Tooltip title={perMilleHint(value)} placement="top-end">
      <Box sx={{ minWidth: 190 }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography sx={{ fontSize: 11, color: tokens.inkMuted }}>
            per 1000 impressions
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: value === null ? tokens.inkFaint : tokens.ink,
              fontVariantNumeric: "tabular-nums",
            }}
            data-testid={`per-mille-${offerId}`}
          >
            {formatPerMille(value, currency)}
          </Typography>
        </Stack>
        <Box
          sx={{
            height: 6,
            mt: 0.5,
            borderRadius: "2px",
            bgcolor: tokens.rowAlt,
            overflow: "hidden",
          }}
        >
          {value !== null && best > 0 && (
            <Box
              sx={{
                height: "100%",
                width: `${(value / best) * 100}%`,
                bgcolor: tokens.vizMoney,
                borderRadius: "0 4px 4px 0",
              }}
            />
          )}
        </Box>
      </Box>
    </Tooltip>
  );
}

/**
 * The table view — every figure the charts round away, in one grid.
 *
 * Not a duplicate of the strips: a chart is read for shape and a table is read
 * for a number somebody is about to put in a spreadsheet, and the accessibility
 * contract for any colour-encoded chart is that the same data exists as text.
 * This is the shape the CSV serialises, column for column.
 */
function OfferTable({
  rows,
  chain,
}: {
  rows: OfferFunnelRow[];
  chain: boolean;
}) {
  return (
    <TableContainer>
      <Table size="small" data-testid="revenue-offer-table">
        <TableHead>
          <TableRow>
            <TableCell>Offer</TableCell>
            {chain && <TableCell align="right">Hotels</TableCell>}
            <TableCell align="right">Impressions</TableCell>
            <TableCell align="right">
              <Tooltip title={ENGAGED_PROXY_NOTE}>
                <span>Engaged (≥5s)</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">Opened</TableCell>
            <TableCell align="right">Requested</TableCell>
            <TableCell align="right">Accepted</TableCell>
            <TableCell align="right">Declined</TableCell>
            <TableCell align="right">Pending</TableCell>
            <TableCell align="right">Refunded</TableCell>
            <TableCell align="right">Revenue</TableCell>
            <TableCell align="right">
              <Tooltip title="Pipeline, not revenue. Never added to the column on its left.">
                <span>Pipeline</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">Per 1000</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.offerId} data-testid={`row-${row.offerId}`}>
              <TableCell>{row.label}</TableCell>
              {chain && (
                <TableCell align="right">{premiseCountOf(row) ?? ""}</TableCell>
              )}
              <Num value={row.impressions.toLocaleString("sv-SE")} />
              <Num
                value={`${row.engaged.toLocaleString("sv-SE")} · ${formatPercent(row.engagedRate)}`}
              />
              <Num value={row.opens.toLocaleString("sv-SE")} />
              <Num value={row.requests.toLocaleString("sv-SE")} />
              <Num value={row.accepted.toLocaleString("sv-SE")} />
              <Num value={row.declined.toLocaleString("sv-SE")} />
              <Num value={row.pending.toLocaleString("sv-SE")} />
              <Num value={row.refunded.toLocaleString("sv-SE")} />
              <Num
                value={
                  moneyIsEmpty(row.revenue)
                    ? "0"
                    : formatMoneyTotals(row.revenue)
                }
                strong
              />
              <Num
                value={
                  moneyIsEmpty(row.pipeline)
                    ? "0"
                    : formatMoneyTotals(row.pipeline)
                }
              />
              <Num
                value={formatPerMille(
                  row.revenuePerMille,
                  row.revenuePerMilleCurrency
                )}
                muted={row.revenuePerMille === null}
              />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function Num({
  value,
  strong,
  muted,
}: {
  value: string;
  strong?: boolean;
  muted?: boolean;
}) {
  return (
    <TableCell
      align="right"
      sx={{
        fontVariantNumeric: "tabular-nums",
        fontWeight: strong ? 700 : undefined,
        color: muted ? tokens.inkFaint : undefined,
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </TableCell>
  );
}

/* -------------------------------------------------------------------------- */
/* The chain layer                                                            */
/* -------------------------------------------------------------------------- */

/**
 * One row per hotel, best-earning first.
 *
 * Ranked on the largest single-currency revenue subtotal, re-asserted here so a
 * caller that hands the page an unsorted list still answers the question a
 * group actually asks: which of my properties is getting value out of this, and
 * what are the good ones doing differently.
 *
 * **The comparator is character-for-character the server's**
 * (`offerFunnelReport.chainOfferFunnel`), and it has to stay that way. Ties on
 * revenue are the common case rather than the exotic one — every property that
 * earned nothing is tied at zero — so a different tie-break here would order
 * the screen one way and the CSV (which serialises the server's order) another,
 * off the same data, in the same window. Requests before impressions, so a
 * hotel whose offers are free still ranks by how much they are used.
 */
function PremiseRanking({ data }: { data: ChainOfferFunnelSummary }) {
  const ranked = [...data.premises].sort(
    (a, b) =>
      (primaryMoney(b.totals.revenue)?.amount ?? 0) -
        (primaryMoney(a.totals.revenue)?.amount ?? 0) ||
      b.totals.requests - a.totals.requests ||
      b.totals.impressions - a.totals.impressions ||
      a.premiseName.localeCompare(b.premiseName)
  );
  return (
    <SectionCard
      title="Hotel by hotel"
      caption="Best-earning first. A hotel with impressions and no revenue is not a broken report — it is the property to ring."
    >
      {ranked.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
          No hotels of this customer are visible to your account. Offers and
          revenue are hotel-only — care locations never appear here.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" data-testid="revenue-premise-table">
            <TableHead>
              <TableRow>
                <TableCell>Hotel</TableCell>
                <TableCell align="right">Revenue</TableCell>
                <TableCell align="right">Pipeline</TableCell>
                <TableCell align="right">Per 1000</TableCell>
                <TableCell align="right">Impressions</TableCell>
                <TableCell align="right">Requested</TableCell>
                <TableCell align="right">Accepted</TableCell>
                <TableCell>Best-earning offer</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ranked.map((p) => (
                <TableRow
                  key={p.premiseId}
                  data-testid={`hotel-${p.premiseId}`}
                >
                  <TableCell>{p.premiseName}</TableCell>
                  <Num
                    value={
                      moneyIsEmpty(p.totals.revenue)
                        ? "0"
                        : formatMoneyTotals(p.totals.revenue)
                    }
                    strong
                  />
                  <Num
                    value={
                      moneyIsEmpty(p.totals.pipeline)
                        ? "0"
                        : formatMoneyTotals(p.totals.pipeline)
                    }
                  />
                  <Num
                    value={formatPerMille(
                      p.totals.revenuePerMille,
                      p.totals.revenuePerMilleCurrency
                    )}
                    muted={p.totals.revenuePerMille === null}
                  />
                  <Num value={p.totals.impressions.toLocaleString("sv-SE")} />
                  <Num value={p.totals.requests.toLocaleString("sv-SE")} />
                  <Num value={p.totals.accepted.toLocaleString("sv-SE")} />
                  <TableCell>
                    {p.topOffer ? (
                      p.topOffer.label
                    ) : (
                      <Typography
                        component="span"
                        sx={{ fontSize: 13, color: tokens.inkFaint }}
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/* Shell pieces                                                               */
/* -------------------------------------------------------------------------- */

function SectionCard({
  title,
  caption,
  action,
  children,
}: {
  title: string;
  caption?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card variant="outlined" sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
        sx={{ mb: caption ? 0.5 : 2 }}
      >
        <Typography sx={{ fontWeight: 700, color: tokens.ink }}>
          {title}
        </Typography>
        {action}
      </Stack>
      {caption && (
        <Typography sx={{ fontSize: 12, color: tokens.inkMuted, mb: 2.5 }}>
          {caption}
        </Typography>
      )}
      {children}
    </Card>
  );
}

/**
 * The state this page will be in more often than any other in the first year.
 *
 * It teaches, in the register POS-5's Offers pane set: say what each thing
 * means in one sentence each, and say what the page will show once there is
 * something to show — rather than drawing a blank chart, which reads as a
 * broken feature and is the single fastest way to lose a hotel's interest in
 * the first week.
 */
function TeachingEmptyState() {
  return (
    <Card variant="outlined" sx={{ p: 4 }} data-testid="revenue-empty">
      <Stack spacing={1.5} sx={{ maxWidth: 640 }}>
        <Typography sx={{ fontWeight: 700, color: tokens.ink, fontSize: 18 }}>
          Nothing has been shown, and nothing has been asked for
        </Typography>
        <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
          Once a television has put an offer in front of a guest, this page
          answers three questions in order.{" "}
          <strong>How often it was shown</strong> — impressions, counted on the
          screens themselves.{" "}
          <strong>How many of those turned into a request</strong> — a guest
          picking something from their room.{" "}
          <strong>What reception accepted</strong> — and only that is revenue.
        </Typography>
        <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
          An impression is never revenue, and a request nobody accepted has
          earned nothing. Money that is still waiting is drawn separately, as
          pipeline. That is why the number at the top of this page can be shown
          to an owner.
        </Typography>
        <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
          It fills itself in: the counts arrive from the televisions once a day,
          and the money the moment somebody presses Accept in Requests.
        </Typography>
        <Box>
          <Button
            component={RouterLink}
            to="/frontlayer/offers"
            variant="contained"
            size="small"
            data-testid="revenue-empty-cta"
          >
            Author an offer
          </Button>
        </Box>
      </Stack>
    </Card>
  );
}

/**
 * A direct link to a report somebody may not read.
 *
 * The nav hides the section already, so this is only ever a bookmark or a
 * pasted URL — and it names the missing right rather than showing an empty
 * page, because an empty report and a forbidden one look identical and only one
 * of them is worth telling somebody about.
 */
function NotYourReport() {
  return (
    <PageContainer>
      <PageHeader eyebrow="Promotions" title="Revenue" />
      <Card variant="outlined" sx={{ p: 4 }}>
        <Stack spacing={1} alignItems="flex-start">
          <Typography sx={{ fontWeight: 700, color: tokens.ink }}>
            You cannot read this report
          </Typography>
          <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
            Reading what the offers earned needs <strong>Manage offers</strong>{" "}
            or <strong>Handle guest requests</strong>. Ask an administrator to
            add one of them to your role.
          </Typography>
        </Stack>
      </Card>
    </PageContainer>
  );
}
